/**
 * SignContractPageV2 — the public v2 signing-ceremony SHELL (CB-5.1).
 *
 * A fully public, token-authenticated page: no login, no app chrome, no CRM machinery. Rendered by
 * the CB-1.6 `/sign` dispatcher when `contract_model='v2'` (AD-11). It authenticates the external
 * signer by their per-signer access_token alone (AD-6) via the token-scoped `get_signing_context_v2`
 * RPC run as service_role (AD-9), and returns ONLY that signer's own data — never sibling signers'.
 *
 * SCOPE: CB-5.1 established this shell (layout/branding — trust-forward register, UX-DR4/UX-DR16 —
 * signer identity, progress + sticky bar, graceful terminal/invalid-link messaging UX-DR20/NFR-5).
 * The full Epic 5 ceremony is now mounted into it: the consent gate (CB-5.3), self-attestation
 * (CB-5.2), document review (CB-5.4), signature pad (CB-5.5), guided field filling (CB-5.6), decline
 * (CB-5.7), consent withdrawal (CB-5.8), and completion (CB-5.10) are all wired via the step machine.
 *
 * Accessibility floor (NFR-14): the app viewport already permits zoom (no maximum-scale=1 /
 * user-scalable=no); this page adds ≥44px action targets, visible focus rings, ≥16px base type, and
 * a single-column layout that reflows at 320px with no horizontal scroll.
 */

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import { contractService } from '@/services/contractService';
import type { SigningContextV2, SigningTenantBrandingV2 } from '@/types/contracts';
import { PRESET_THEMES, DEFAULT_THEME } from '@/types/theme';
import SelfAttestation from './SelfAttestation';
import ConsentGate from './ConsentGate';
import SignatureAdoption from './SignatureAdoption';
import FieldFiller from './FieldFiller';
import DeclineToSign from './DeclineToSign';
import WithdrawConsent from './WithdrawConsent';
import CompletionScreen from './CompletionScreen';
import { type SignatureValue, hasSignature, fontForStyle } from '@/components/settings/contracts/storedSignature';
import { rehydrateAdoptedSignature } from './resumeSignature';
import { contrastSafeAccent, accentTextOnLight } from '@/lib/tenantAccent';

interface SignContractPageV2Props {
  /** Per-signer access_token off the signing link (AD-6). */
  token: string;
  /** Optional context already loaded by the dispatcher — avoids a second RPC round-trip. When
   *  absent/null the page self-loads by token (the story's "read token from URL, call loader"). */
  initialContext?: SigningContextV2 | null;
}

// --- Tenant-accent resolution (the WCAG contrast guard lives in the shared lib/tenantAccent util,
//     UX-DR3 — reused by the ceremony's binding controls and the editor's tenant-primary surfaces) ---

/** Resolve the single tenant accent from the theme id + custom themes, reusing the same lookup
 *  ThemeContext uses ([...PRESET_THEMES, ...customThemes].find by id). Falls back to the neutral
 *  Joules default so the primary control always has a sensible, legible accent. */
function resolveTenantAccent(branding: SigningTenantBrandingV2): string {
  const all = [...PRESET_THEMES, ...(branding.custom_themes ?? [])];
  const match = branding.theme_id ? all.find((t) => t.id === branding.theme_id) : undefined;
  return match?.colors?.primary ?? DEFAULT_THEME.colors.primary;
}

// --- Terminal-state derivation (Task 5 / AC3) ---

type CeremonyState =
  | 'loading'
  | 'error' // transport/RPC failure — recoverable
  | 'invalid' // unknown/blank token or non-v2 contract — generic, no leak
  | 'expired'
  | 'voided'
  | 'declined'
  | 'consent_withdrawn'
  | 'already_signed'
  | 'active';

function deriveState(ctx: SigningContextV2): CeremonyState {
  if (ctx.status === 'invalid') return 'invalid';
  const { contract, signer } = ctx;
  // The signer already finished their part — reassure (download arrives with CB-5.10).
  if (signer.signer_status === 'signed') return 'already_signed';
  // CB-5.8: a signer who withdrew ESIGN consent is halted (server-side too) — show the withdrawn
  // state. Withdrawal does not change envelope_status, so this must be checked explicitly.
  if (signer.consent_withdrawn_at) return 'consent_withdrawn';
  // Envelope terminal states (mirror signing_service.TERMINAL_ENVELOPE). Past-due expires_at is
  // treated as expired even if the expiry cron has not yet flipped the status (defense-in-depth).
  if (contract.envelope_status === 'voided') return 'voided';
  if (contract.envelope_status === 'declined' || signer.signer_status === 'declined') return 'declined';
  if (contract.envelope_status === 'expired') return 'expired';
  if (contract.expires_at && new Date(contract.expires_at).getTime() <= Date.now()) return 'expired';
  return 'active';
}

// --- Presentational pieces ---

/** Tenant logo + name header (UX-DR16). Logo if present, else an accent initial badge. No Joules
 *  branding — the signer sees the contracting company (UX-DR4). */
function BrandHeader({
  companyName,
  logo,
  accent,
  subline,
}: {
  companyName: string;
  logo: string | null;
  accent: string;
  subline: string;
}) {
  return (
    <header className="flex items-center gap-3">
      {logo ? (
        <img
          src={logo}
          alt={`${companyName} logo`}
          className="h-10 w-auto max-w-[160px] object-contain"
        />
      ) : (
        <span
          aria-hidden="true"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg font-bold"
          style={{ backgroundColor: accent, color: contrastSafeAccent(accent).foreground }}
        >
          {(companyName.trim()[0] || '•').toUpperCase()}
        </span>
      )}
      <div className="min-w-0">
        <div className="truncate text-base font-semibold text-slate-900">{companyName}</div>
        <div className="truncate text-sm text-slate-500">{subline}</div>
      </div>
    </header>
  );
}

/** A calm, centered frame for a single terminal/invalid message — never a raw error (UX-DR20). */
function MessageFrame({
  companyName,
  logo,
  accent,
  icon,
  title,
  body,
}: {
  companyName: string | null;
  logo: string | null;
  accent: string;
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <main className="flex min-h-screen flex-col items-center bg-slate-50 px-4 py-10 text-slate-900">
      <div className="w-full max-w-[640px]">
        {companyName ? (
          <BrandHeader companyName={companyName} logo={logo} accent={accent} subline="contract signing" />
        ) : null}
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-500">
            {icon}
          </div>
          <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
          <p className="mt-2 text-base leading-relaxed text-slate-600">{body}</p>
        </div>
      </div>
    </main>
  );
}

export default function SignContractPageV2({ token, initialContext }: SignContractPageV2Props) {
  const [searchParams] = useSearchParams();
  // Prefer the explicit prop; fall back to the URL so the page is self-sufficient (story Task 3).
  const effectiveToken = token || searchParams.get('token') || '';

  const [context, setContext] = useState<SigningContextV2 | null>(initialContext ?? null);
  const [isLoading, setIsLoading] = useState<boolean>(!initialContext);
  const [loadError, setLoadError] = useState<boolean>(false);
  // CB-5.2/5.3: flip true when the signer completes that gate in this session (the loaded context
  // carries the persisted state for resume). These drive the ceremony step below.
  const [attestedThisSession, setAttestedThisSession] = useState<boolean>(false);
  const [consentedThisSession, setConsentedThisSession] = useState<boolean>(false);
  // CB-5.5: the adopted signature, held in ceremony session state. CB-5.6 applies it to the
  // signer's own fields (tap-to-apply / "apply to all my fields") via the token-scoped fill RPC.
  const [adoptedSignature, setAdoptedSignature] = useState<SignatureValue | null>(null);
  // CB-5.7: flips true after a successful decline so the page renders the terminal declined state.
  const [declinedThisSession, setDeclinedThisSession] = useState<boolean>(false);
  // CB-5.8: flips true after a successful consent withdrawal → terminal withdrawn state.
  const [withdrawnThisSession, setWithdrawnThisSession] = useState<boolean>(false);
  // CB-5.10: flips true after a successful Finish → terminal completion screen.
  const [signedThisSession, setSignedThisSession] = useState<boolean>(false);
  // CB-5.9: guards the one-time resume probe (below) so it fires at most once per token.
  const resumeProbedRef = useRef(false);

  useEffect(() => {
    // Dispatcher already handed us the context — no second round-trip needed.
    if (initialContext) {
      setContext(initialContext);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      setLoadError(false);
      if (!effectiveToken) {
        if (!cancelled) {
          setContext({ status: 'invalid' });
          setIsLoading(false);
        }
        return;
      }
      const result = await contractService.getSigningContextV2(effectiveToken);
      if (cancelled) return;
      if (!result.success || !result.data) {
        setLoadError(true);
        setIsLoading(false);
        return;
      }
      setContext(result.data);
      setIsLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [effectiveToken, initialContext]);

  // CB-5.9 RESUME (FR-54 / UX-DR21): the adopted signature lives only in this page's session state, so
  // on a fresh load it is null and the step machine would route a returning signer back to `adopt`
  // even though they already applied their signature to fields. Rehydrate it from their OWN
  // already-filled signature/initials field (persisted server-side by CB-5.6) so they resume in
  // `review`. Field VALUES themselves rehydrate inside FieldFiller from the same token-scoped read.
  //
  // Gated to a genuine resume: only when persisted consent + self-attest timestamps are set (a fresh
  // session never sees them — they only flip in-memory). This avoids any extra field read in the
  // first-time flow, and the probe runs at most once per token (resumeProbedRef). The read is
  // token-scoped (own fields only) and read-only — no write, no migration, no Fly redeploy.
  useEffect(() => {
    if (resumeProbedRef.current) return;
    if (!context || context.status !== 'ok') return;
    if (deriveState(context) !== 'active') return;
    const s = context.signer;
    if (!s.consent_accepted_at || !s.self_attested_at) return; // not a resume past adoption
    if (adoptedSignature) return; // already adopted/rehydrated this session
    let cancelled = false;
    (async () => {
      const r = await contractService.getSigningFieldsV2(effectiveToken);
      if (cancelled) return;
      // Only burn the once-guard on a SUCCESSFUL read — a transient failure must not permanently
      // strand the signer on `adopt` (re-adopting would write a second signature). A later render
      // (the effect deps haven't changed, but context/adoptedSignature may) can re-attempt.
      if (!r.success || !r.data) return;
      resumeProbedRef.current = true;
      const sig = rehydrateAdoptedSignature(r.data);
      if (sig) setAdoptedSignature(sig);
    })();
    return () => {
      cancelled = true;
    };
  }, [context, effectiveToken, adoptedSignature]);

  // Branding is only known once a valid context loads; messages before that use a neutral frame.
  const branding = context && context.status === 'ok' ? context.tenant : null;
  const signerInfo = context && context.status === 'ok' ? context.signer : null;
  // UX-DR3: run the raw tenant accent through the shared contrast guard ONCE. `accent` (the guarded,
  // possibly-darkened background) + `accentForeground` (a label guaranteed ≥4.5:1) thread to every
  // primary control below — Finish, consent, attest, adopt, Download, the brand badge — so the
  // binding controls can never ship failing contrast even for a light/mid-tone tenant brand.
  const rawAccent = branding ? resolveTenantAccent(branding) : DEFAULT_THEME.colors.primary;
  const { background: accent, foreground: accentForeground } = contrastSafeAccent(rawAccent);

  // --- Loading splash (lightweight, branded-neutral — perceived speed on mobile) ---
  if (isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-50" aria-busy="true">
        <div
          className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200"
          style={{ borderTopColor: accent }}
          role="status"
          aria-label="Loading your contract"
        />
      </main>
    );
  }

  // --- Recoverable transport/RPC error (never a raw error string to the signer) ---
  if (loadError || !context) {
    return (
      <MessageFrame
        companyName={null}
        logo={null}
        accent={accent}
        icon={<AlertTriangle className="h-7 w-7" aria-hidden="true" />}
        title="We couldn't load your contract"
        body="Something went wrong on our end. Please refresh the page, or contact the sender if this keeps happening."
      />
    );
  }

  const state = deriveState(context);
  const companyName = branding?.company_name ?? 'The sender';
  const logo = branding?.logo ?? null;

  // --- Terminal / invalid states: specific, recoverable copy (UX-DR20, AC3) ---
  if (state === 'invalid') {
    return (
      <MessageFrame
        companyName={null}
        logo={null}
        accent={accent}
        icon={<AlertTriangle className="h-7 w-7" aria-hidden="true" />}
        title="This link isn't valid"
        body="This signing link is invalid or has been replaced. If you were expecting to sign a contract, contact the sender for a new link."
      />
    );
  }
  if (state === 'expired') {
    return (
      <MessageFrame
        companyName={companyName}
        logo={logo}
        accent={accent}
        icon={<AlertTriangle className="h-7 w-7" aria-hidden="true" />}
        title="This signing link has expired"
        body={`This signing link has expired. Contact ${companyName} to request a new one.`}
      />
    );
  }
  if (state === 'voided') {
    return (
      <MessageFrame
        companyName={companyName}
        logo={logo}
        accent={accent}
        icon={<AlertTriangle className="h-7 w-7" aria-hidden="true" />}
        title="This contract was cancelled"
        body={`${companyName} cancelled this contract, so there's nothing further to sign. Contact them if you have questions.`}
      />
    );
  }
  if (state === 'declined') {
    return (
      <MessageFrame
        companyName={companyName}
        logo={logo}
        accent={accent}
        icon={<AlertTriangle className="h-7 w-7" aria-hidden="true" />}
        title="This contract was declined"
        body={`This contract was declined and is no longer available to sign. Contact ${companyName} if you believe this is a mistake.`}
      />
    );
  }
  // CB-5.10: a signer who already finished (this session or a prior visit) sees the reassuring
  // completion screen with the "Download your copy" affordance (FR-55) — not a bare message.
  if (state === 'already_signed' && signerInfo) {
    return (
      <CompletionScreen
        companyName={companyName}
        logo={logo}
        accent={accent}
        ink={accentForeground}
        signerName={signerInfo.name}
        signerEmail={signerInfo.email}
        token={effectiveToken}
      />
    );
  }
  if (state === 'consent_withdrawn') {
    return (
      <MessageFrame
        companyName={companyName}
        logo={logo}
        accent={accent}
        icon={<AlertTriangle className="h-7 w-7" aria-hidden="true" />}
        title="You withdrew electronic consent"
        body={`This contract can't be completed electronically. ${companyName} has been notified and can arrange a paper copy with you.`}
      />
    );
  }

  // CB-5.7: a decline that just happened in this session → terminal declined end-state (no re-sign).
  if (declinedThisSession) {
    return (
      <MessageFrame
        companyName={companyName}
        logo={logo}
        accent={accent}
        icon={<AlertTriangle className="h-7 w-7" aria-hidden="true" />}
        title="You declined to sign"
        body={`Thanks for letting us know. ${companyName} has been notified that you declined, and this contract has been halted.`}
      />
    );
  }
  // CB-5.8: a consent withdrawal that just happened → terminal withdrawn end-state.
  if (withdrawnThisSession) {
    return (
      <MessageFrame
        companyName={companyName}
        logo={logo}
        accent={accent}
        icon={<AlertTriangle className="h-7 w-7" aria-hidden="true" />}
        title="You withdrew electronic consent"
        body={`This contract can't be completed electronically. ${companyName} has been notified and can arrange a paper copy with you.`}
      />
    );
  }
  // CB-5.10: a Finish that just succeeded → reassuring completion screen (the seal/email is the
  // invisible Epic-6 follow-up; the signer is told "done" immediately, FR-55/UX-DR20).
  if (signedThisSession && signerInfo) {
    return (
      <CompletionScreen
        companyName={companyName}
        logo={logo}
        accent={accent}
        ink={accentForeground}
        signerName={signerInfo.name}
        signerEmail={signerInfo.email}
        token={effectiveToken}
      />
    );
  }

  // --- Active ceremony shell (state === 'active') ---
  // context.status is 'ok' here (every non-'ok' path returned above), so signer/contract are present.
  const ctx = context as Extract<SigningContextV2, { status: 'ok' }>;
  const signer = ctx.signer;
  const contractLabel = ctx.contract.contract_name || ctx.contract.type || 'a contract';
  const greetingName = (signer.name || '').trim().split(' ')[0] || 'there';
  const ink = accentForeground;

  // Ceremony step machine (AD-16 flow: consent → self-attest → review/fields). On resume the loaded
  // context's persisted *_at timestamps skip already-completed gates. Document review (CB-5.4) and
  // guided fields (CB-5.6) build out the 'review' phase.
  const hasConsented = Boolean(signer.consent_accepted_at) || consentedThisSession;
  const hasAttested = Boolean(signer.self_attested_at) || attestedThisSession;
  const hasAdopted = adoptedSignature !== null && hasSignature(adoptedSignature);
  const step: 'consent' | 'attest' | 'adopt' | 'review' = !hasConsented
    ? 'consent'
    : !hasAttested
      ? 'attest'
      : !hasAdopted
        ? 'adopt'
        : 'review';

  return (
    <main className="flex min-h-screen flex-col bg-slate-50 text-slate-900">
      {/* Centered ~640px column with generous rhythm; pb leaves room for the sticky action bar. */}
      <div className="mx-auto w-full max-w-[640px] flex-1 px-4 pb-32 pt-8">
        <BrandHeader
          companyName={companyName}
          logo={logo}
          accent={accent}
          subline="sent you a contract to sign"
        />

        <section className="mt-8">
          <h1 className="text-2xl font-bold leading-tight text-slate-900">
            Hi {greetingName} — review &amp; sign
          </h1>
          <p className="mt-1 text-base text-slate-600">{contractLabel}</p>
          <p className="mt-1 text-sm text-slate-500">
            Signing as {signer.name || 'you'} · {signer.email}
          </p>
        </section>

        {/* Progress indicator (UX-DR16): a stable not-started baseline for the pre-fill steps. In the
            review step FieldFiller renders the REAL "X of N required complete" progressbar, so this
            placeholder is hidden there — otherwise AT would hear a second progressbar announcing 0%
            while fields are already complete. */}
        {step !== 'review' ? (
          <section className="mt-6" aria-label="Signing progress">
            <div className="flex items-center justify-between text-sm text-slate-500">
              <span>Review &amp; sign to continue</span>
              <span aria-hidden="true">0%</span>
            </div>
            <div
              className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={0}
            >
              <div className="h-full rounded-full transition-all" style={{ width: '0%', backgroundColor: accent }} />
            </div>
          </section>
        ) : null}

        {/* ====================================================================
            Ceremony steps — built out across Epic 5. Self-attestation (CB-5.2) is
            the first interactive step. Remaining slots, in flow order, are stable
            mount points so later stories slot in without reshaping the shell:
              · CB-5.3  ESIGN §7001(c) consent gate    → ConsentGate / AccessDemonstration (before attest)
              · CB-5.4  document review (zoomable PDF)  → DocumentReview
              · CB-5.5  adopt a signature              → SignaturePad
              · CB-5.6  guided field completion        → FieldFiller
              · CB-5.7  decline to sign                → decline control (in the action bar slot)
            ==================================================================== */}
        {step === 'consent' ? (
          <ConsentGate
            companyName={companyName}
            token={effectiveToken}
            accent={accent}
            ink={ink}
            onConsented={() => setConsentedThisSession(true)}
          />
        ) : step === 'attest' ? (
          <SelfAttestation
            signerName={signer.name}
            token={effectiveToken}
            accent={accent}
            ink={ink}
            onAttested={() => setAttestedThisSession(true)}
          />
        ) : step === 'adopt' ? (
          <SignatureAdoption
            signerName={signer.name}
            accent={accent}
            ink={ink}
            onAdopt={(value) => setAdoptedSignature(value)}
          />
        ) : (
          // CB-5.6: guided field completion over the CB-5.4 document — applies the adopted signature,
          // gates Finish, auto-fills Date-Signed. FieldFiller owns the sticky action bar.
          <section className="mt-6" aria-label="Contract" data-ceremony-slot="document-and-fields">
            {adoptedSignature ? (
              <div className="mb-4 flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
                <span className="text-sm text-slate-500">Your signature</span>
                {adoptedSignature.signature_image ? (
                  <img
                    src={adoptedSignature.signature_image}
                    alt="Your adopted signature"
                    className="h-9 max-w-[200px] object-contain"
                  />
                ) : (
                  <span
                    className="truncate text-2xl leading-none text-slate-900"
                    style={{ fontFamily: fontForStyle(adoptedSignature.signature_style) }}
                  >
                    {adoptedSignature.signature_typed}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setAdoptedSignature(null)}
                  className="ml-auto rounded-lg px-2 py-1 text-sm font-medium underline focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  style={{ color: accentTextOnLight(accent) }}
                >
                  Change
                </button>
              </div>
            ) : null}
            <FieldFiller
              token={effectiveToken}
              adoptedSignature={adoptedSignature}
              accent={accent}
              ink={ink}
              onFinish={async () => {
                // CB-5.10: the REAL signer-submit. On success the completion screen replaces the
                // ceremony; on failure FieldFiller surfaces the SERVER's reason (e.g. "expired").
                const result = await contractService.signContractV2(effectiveToken);
                if (result.success) {
                  setSignedThisSession(true);
                  return { ok: true };
                }
                return { ok: false, error: result.error };
              }}
            />
          </section>
        )}

        {/* CB-5.7 (UX-DR20): Decline is first-class and reachable from EVERY step. The guided-fields
            sticky action bar (Next field / progress / Finish) is owned by FieldFiller in review. */}
        <DeclineToSign token={effectiveToken} accent={accent} onDeclined={() => setDeclinedThisSession(true)} />

        {/* CB-5.8 (FR-75): the operable consent-withdrawal mechanism — available only once the signer
            has consented (post the CB-5.3 gate). Honest, non-buried, secondary to the affirmative flow. */}
        {hasConsented ? (
          <WithdrawConsent
            token={effectiveToken}
            companyName={companyName}
            accent={accent}
            onWithdrawn={() => setWithdrawnThisSession(true)}
          />
        ) : null}

        <p className="mt-6 flex items-center justify-center gap-1.5 text-center text-xs text-slate-400">
          <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
          Secure signing · no account required
        </p>
      </div>
    </main>
  );
}
